import React, { useContext, useState, Fragment } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { styled } from '@mui/material';

import {
    Box,
    Stack,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CompletionButton,
    Tag,
    IconButton,
    Tabs,
} from 'shell/components';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/PeerEvaluation/PageProvider';
import { getUserPreferredFullName } from 'utils/helper';
import {
    RubricGiven,
    RubricReceived,
    ZeroSumReceived,
    ZeroSumGiven,
    analyzeAlerts,
    RenderAlerts,
} from './PeerEvaluationComponents';
import { useAuth } from 'contexts/auth';

const ViewSubmissionsDesktopTablet = ({ students, submissionList = [], groups }) => {
    const { auth } = useAuth();
    const { device } = useViewport();
    const {
        currentPeerEvaluation,
        handleOpenDialog,

        // Constants
        DIALOG,
    } = useContext(PageContext);

    const [selectedStudent, setSelectedStudent] = useState('');
    const [defaultRoute, setDefaultRoute] = useState('received');

    const handleOpenSubmissionTable = (owner_account_id) => {
        if (selectedStudent === owner_account_id) {
            return setSelectedStudent('');
        }
        return setSelectedStudent(owner_account_id);
    };

    const handleStudentNotication = (owner_account_id) => {
        const findStudent = students.find((student) => student.owner_account_id === owner_account_id);
        return handleOpenDialog(DIALOG.PEER_EVALUATION_NOTIFICATIONS_TO_STUDENT, {
            id: currentPeerEvaluation.id,
            accounts: [findStudent],
        });
    };

    const props = {
        device,
        isInstructor: auth.role === 'instructor',
        currentPeerEvaluation,
        selectedStudent,
        students,
        submissionList,
    };

    const renderReceived = () => {
        return currentPeerEvaluation.format.value === 'rubric' ? (
            <RubricReceived {...props} />
        ) : (
            <ZeroSumReceived {...props} />
        );
    };

    const renderGiven = () => {
        return currentPeerEvaluation.format.value === 'rubric' ? (
            <RubricGiven {...props} />
        ) : (
            <ZeroSumGiven {...props} />
        );
    };

    const tabList = [
        {
            key: 'received',
            value: 'Received',
            content: renderReceived(),
        },
        {
            key: 'given',
            value: 'Given',
            content: renderGiven(),
        },
    ];

    const renderTabs = () => {
        return (
            <Tabs
                aria-label={'select rubric peer evaluation option to view submission'}
                options={tabList}
                selected={defaultRoute}
                onChange={setDefaultRoute}
            />
        );
    };

    return (
        <TableOuterBox device={device}>
            <TableInnerBox device={device}>
                <CustomTable device={device} size={'small'}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Team</TableCell>
                            <TableCell>Submitted On</TableCell>
                            <TableCell>Alerts</TableCell>
                            <TableCell>Completion</TableCell>
                            <TableCell>
                                <Stack alignItems={'center'}>
                                    <Tooltip
                                        content={
                                            'If the due date is approaching and students haven’t submitted, you can send them a reminder email by clicking the bell. '
                                        }
                                        type={'info'}
                                    />
                                </Stack>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(students || [])
                            .filter((student) => student.group_id)
                            .map((student) => {
                                const findStudentSubmission = submissionList.find(
                                    (submission) => submission.owner_account_id === student.owner_account_id
                                );
                                const findGroupSubmissions = submissionList.filter(
                                    (submission) => submission.data?.[student.owner_account_id]
                                );
                                const findGroup = groups.find((group) => group.id === student.group_id);
                                const findAlerts = analyzeAlerts(
                                    student.owner_account_id,
                                    findGroupSubmissions,
                                    currentPeerEvaluation.format.value === 'rubric'
                                );
                                return (
                                    <Fragment key={student.id}>
                                        <TableRow
                                            sx={
                                                selectedStudent === student.owner_account_id
                                                    ? { '& > *': { borderBottom: 'unset' } }
                                                    : {}
                                            }
                                            onClick={() => handleOpenSubmissionTable(student.owner_account_id)}
                                        >
                                            <TableCell noWrap>
                                                <Stack
                                                    data-cy={'view-submission-student-name'}
                                                    alignItems={'center'}
                                                    direction={'row'}
                                                    spacing={2}
                                                >
                                                    <>{getUserPreferredFullName(student)}</>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{findGroup.name}</TableCell>
                                            <TableCell>
                                                {findStudentSubmission
                                                    ? moment(findStudentSubmission.created_on).format(
                                                          'MMM D [at] hh:mm a'
                                                      )
                                                    : '--'}
                                            </TableCell>
                                            <TableCell>
                                                {findAlerts?.length > 0 && (
                                                    <Tooltip content={RenderAlerts(findAlerts)} type={'alert'} />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                                    <CompletionButton
                                                        aria-label={
                                                            findStudentSubmission ? 'completed' : 'not completed'
                                                        }
                                                        checked={findStudentSubmission}
                                                    />
                                                    {findStudentSubmission?.created_on >
                                                        currentPeerEvaluation.due_on && (
                                                        <Tag type={'submission'} value={'late'} />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Stack alignItems={'center'}>
                                                    {!findStudentSubmission && (
                                                        <IconButton
                                                            aria-label={'notify the student'}
                                                            kind={'bell'}
                                                            size={'medium'}
                                                            variant={'square'}
                                                            onClick={() =>
                                                                handleStudentNotication(student.owner_account_id)
                                                            }
                                                        />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                        {selectedStudent === student.owner_account_id && (
                                            <TableRow
                                                sx={{
                                                    height: 'fit-content',
                                                    width: '100%',
                                                }}
                                            >
                                                <TableCell colSpan={6} sx={{ height: '100%', px: '0 !important' }}>
                                                    <SubmissionGradeBox>{renderTabs()}</SubmissionGradeBox>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                );
                            })}
                    </TableBody>
                </CustomTable>
            </TableInnerBox>
        </TableOuterBox>
    );
};

const TableOuterBox = styled(Box)(({ device, theme }) => {
    return {
        height: '100%',
        backgroundColor: theme.palette.ensightful.shades[0],
        borderRadius: '12px',
        ...(device === 'mobile' && {
            margin: theme.spacing(0, -5),
            width: '100vw',
        }),
        ...(device === 'tablet' && {
            margin: theme.spacing(0, -5),
        }),
    };
});

const TableInnerBox = styled(Box)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            width: 'fit-content',
            padding: theme.spacing(0, 5),
        }),
        ...(device === 'tablet' && {
            width: 'fit-content',
            padding: theme.spacing(0, 5),
        }),
    };
});

const CustomTable = styled(Table)(({ device, theme }) => {
    return {
        ...(device !== 'mobile' && {
            tableLayout: 'fixed',
            '& > thead > tr > th:nth-of-type(1)': {
                width: '25%',
            },
            '& > thead > tr > th:nth-of-type(2)': {
                width: '20%',
            },
            '& > thead > tr > th:nth-of-type(3)': {
                width: '20%',
            },
            '& > thead > tr > th:nth-of-type(4)': {
                width: '10%',
            },
            '& > thead > tr > th:nth-of-type(5)': {
                width: '15%',
            },
            '& > thead > tr > th:nth-of-type(6)': {
                width: '56px',
            },
            '& > tbody > tr > td:nth-of-type(1)': {
                width: '25%',
            },
            '& > tbody > tr > td:nth-of-type(2)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(3)': {
                width: '20%',
            },
            '& > tbody > tr > td:nth-of-type(4)': {
                width: '10%',
            },
            '& > tbody > tr > td:nth-of-type(5)': {
                width: '15%',
            },
            '& > tbody > tr > td:nth-of-type(6)': {
                width: '56px',
            },
        }),
    };
});

const SubmissionGradeBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        height: '100%',
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: theme.spacing(1),
    };
});

export default ViewSubmissionsDesktopTablet;
